import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

export const useToggleOpenState = (
  defaultValue?: boolean | string,
  onToggle?: () => void
): [
  boolean,
  (v?: boolean | React.MouseEvent<HTMLElement>) => void,
  () => void,
  () => void
] => {
  const router = useRouter();
  const { url, hash } = useUrlAndHashFromPath();

  const isBasedOnHash = typeof defaultValue === 'string';
  const [mounted, setMounted] = useState(false);
  const [isOpenState, setIsOpenState] = useState(
    isBasedOnHash ? hash === defaultValue : !!defaultValue
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  function toggle(v?: boolean | React.MouseEvent<HTMLElement>) {
    onToggle?.();
    if (isBasedOnHash) {
      return router.replace(
        hash === defaultValue || v === false ? url : `${url}#${defaultValue}`,
        undefined,
        { scroll: false }
      );
    }
    setIsOpenState(prev => (typeof v === 'boolean' ? v : !prev));
  }

  function open() {
    onToggle?.();
    if (isBasedOnHash) {
      return router.replace(`${url}#${defaultValue}`, undefined, {
        scroll: false,
      });
    }
    setIsOpenState(true);
  }

  function close() {
    onToggle?.();
    if (isBasedOnHash) {
      return router.replace(url, undefined, { scroll: false });
    }
    setIsOpenState(false);
  }

  const isOpen = mounted
    ? isBasedOnHash
      ? hash === defaultValue
      : isOpenState
    : false;

  return [isOpen, toggle, open, close];
};
